import {ApplicationController} from "../../support/application-controller";
import CodeMirror from "codemirror";
import "codemirror/mode/sql/sql"; // SQL syntax highlighting
import "codemirror/addon/hint/sql-hint"; // SQL hints
import "codemirror/addon/hint/show-hint"; // Show autocomplete hints
import "codemirror/lib/codemirror.css"; // CodeMirror basic styles
import "codemirror/addon/hint/show-hint.css"; // Autocomplete hint styling

export default class extends ApplicationController {
    static targets = [
        "queryEditor",
        "resultComment",
        "tableContainer",
        "results",
        "downloadCsvButton",
        "runQueryButton",
        "saveQueryButton",
        "saveQueryButtonText",
        "stopButton",
        "timer",
        "savedQueriesTable",
        "modal",
        "modalTitle",
        "queryForm",
        "queryNameInput",
        "queryDescriptionInput",
        "queryOrgIdInput",
        "modalSubmitButton",
    ];

    isRunning = false;
    currentQueryId = null;
    currentQueryName = null;
    currentQueryDescription = null;
    currentQueryOrgId = null;

    get dbSchema() {
        return this.data.get("dbSchema");
    }

    connect() {
        this.initializeCodeMirror();
        this.initializeExistingQueryRows();
    }

    initializeExistingQueryRows() {
        // Populate action buttons for server-rendered rows
        const rows = this.savedQueriesTableTarget.querySelectorAll(
            "tbody tr[data-query-id]"
        );
        rows.forEach((row) => {
            const actionsCell = row.querySelector(".query-actions");
            if (actionsCell && !actionsCell.hasChildNodes()) {
                const query = {
                    id: row.dataset.queryId,
                    name: row.dataset.queryName,
                    description: row.dataset.queryDescription,
                    organization_id: row.dataset.queryOrgId,
                    organization_name: row.dataset.queryOrgName,
                    query: row.dataset.queryText,
                };
                actionsCell.innerHTML = this.createActionButtons(query);
            }
        });
    }

    initializeCodeMirror() {
        if (this.queryEditorTarget) {
            const dbSchema = JSON.parse(this.dbSchema);

            this.editor = CodeMirror.fromTextArea(this.queryEditorTarget, {
                mode: "text/x-pgsql",
                lineNumbers: true,
                extraKeys: {"Ctrl-Space": "autocomplete"}, // Enable autocomplete
                hintOptions: {
                    tables: dbSchema,
                },
            });

            // Trigger autocomplete while typing
            this.editor.on("inputRead", (editor) => {
                // Only trigger autocomplete when a valid SQL token is typed
                if (editor.state.completionActive) return; // Prevent duplicate triggering
                const cursor = editor.getCursor();
                const token = editor.getTokenAt(cursor);
                if (token.string.trim()) {
                    editor.showHint({completeSingle: false}); // Show hints, but don't auto-complete a single suggestion
                }
            });
        }
    }

    runQuery() {
        this.resultCommentTarget.innerHTML = "";
        this.tableContainerTarget.style.display = "none";
        if (this.isRunning) {
            this.stopQuery().then(() => {
                this.tableContainerTarget.style.display = "none";
                this.runQuery();
            });
            return;
        }
        this.isRunning = true;
        this.updateButtons();
        this.startTimer();
        const query = this.editor.getValue();

        fetch("/api/v1/data/query", {
            method: "POST",
            credentials: "same-origin", // Include cookies with this request
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({sql: query}),
        })
            .then((response) => {
                if (!response.ok) {
                    return response.json().then((data) => {
                        throw new Error(data.error);
                    });
                }

                // Display message from custom header
                this.resultCommentTarget.innerHTML = response.headers.get("X-Message");

                return response.body.getReader();
            })
            .then((reader) => {
                const decoder = new TextDecoder();
                let jsonString = "";

                const readStream = () => {
                    return reader.read().then(({done, value}) => {
                        if (done) {
                            this.stopTimer();
                            this.isRunning = false;
                            this.updateButtons();

                            if (!jsonString.trim()) {
                                this.tableContainerTarget.style.display = "none";
                                return;
                            }

                            const data = JSON.parse(jsonString);
                            // Handle errors and empty results
                            if (data.message) {
                                this.resultCommentTarget.innerHTML = data.message;
                                this.tableContainerTarget.style.display = "none";
                                return;
                            }
                            if (!data || data.error) {
                                throw new Error(data.error || "Unknown error");
                            }
                            if (data.length === 0) {
                                throw new Error("Query returned 0 rows");
                            }

                            // Display results table
                            this.tableContainerTarget.style.display = "block";
                            this.resultsTarget.innerHTML = "";

                            // Create table headers
                            const headers = Object.keys(data[0]);
                            const headerRow = document.createElement("tr");
                            headers.forEach((header) => {
                                const th = document.createElement("th");
                                th.textContent = header;
                                headerRow.appendChild(th);
                            });
                            this.resultsTarget.appendChild(headerRow);

                            // Populate table rows
                            data.forEach((row) => {
                                const tr = document.createElement("tr");
                                headers.forEach((header) => {
                                    const td = document.createElement("td");
                                    td.textContent = row[header];
                                    tr.appendChild(td);
                                });
                                this.resultsTarget.appendChild(tr);
                            });

                            return;
                        }

                        jsonString += decoder.decode(value, {stream: true});
                        return readStream();
                    });
                };

                return readStream();
            })
            .catch((error) => {
                this.resultCommentTarget.innerHTML = error.message || error;
                this.tableContainerTarget.style.display = "none";
                this.stopTimer();
            });
    }

    downloadCsv() {
        if (this.isRunning) {
            this.stopQuery();
            return;
        }

        this.isRunning = true;
        this.updateButtons();
        this.startTimer();
        const query = this.editor.getValue();
        this.tableContainerTarget.style.display = "none";

        fetch("/api/v1/data/query?output-format=csv", {
            method: "POST",
            credentials: "same-origin", // Include cookies with this request
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({sql: query}),
        })
            .then((response) => {
                this.tableContainerTarget.style.display = "none";
                if (!response.ok) {
                    return response.json().then((data) => {
                        throw new Error(data.error);
                    });
                }

                // Try to extract filename from Content-Disposition header
                const disposition = response.headers.get("Content-Disposition");
                let filename = `query_result_${new Date().toISOString()}.csv`; // default filename
                if (disposition && disposition.indexOf("attachment") !== -1) {
                    const filenameMatch = disposition.match(/filename="([^"]+)"/);
                    if (filenameMatch.length > 1) {
                        filename = filenameMatch[1];
                    }
                }
                return response.blob().then((blob) => ({blob, filename}));
            })
            .then(({blob, filename}) => {
                this.resultCommentTarget.innerHTML = "CSV file downloaded";
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.setAttribute("download", filename);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                this.stopTimer();
            })
            .catch((error) => {
                this.resultCommentTarget.innerHTML = error;
                this.stopTimer();
            })
            .finally(() => {
                this.stopTimer();
                this.isRunning = false;
                this.updateButtons();
            });
    }

    startTimer() {
        this.startTime = performance.now();
        this.timerTarget.textContent = "Run time: 0.00s";
        this.timerInterval = setInterval(() => {
            const elapsed = ((performance.now() - this.startTime) / 1000).toFixed(2);
            this.timerTarget.textContent = `Run time: ${elapsed}s`;
        }, 500);
    }

    stopTimer() {
        clearInterval(this.timerInterval);
    }

    stopQuery() {
        if (!this.isRunning) return Promise.resolve();
        return fetch("/api/v1/data/cancel_query", {
            method: "POST",
            credentials: "same-origin",
        }).finally(() => {
            this.stopTimer();
            this.isRunning = false;
            this.updateButtons();
        });
    }

    updateButtons() {
        this.downloadCsvButtonTarget.disabled = this.isRunning;
        this.runQueryButtonTarget.disabled = this.isRunning;
        this.saveQueryButtonTarget.disabled = this.isRunning;
        this.stopButtonTarget.disabled = !this.isRunning;
    }

    saveOrUpdateQuery() {
        const query = this.editor.getValue();
        if (!query.trim()) {
            alert("Please enter a query to save");
            return;
        }

        // Open the modal and populate with current values if updating
        if (this.currentQueryId) {
            this.modalTitleTarget.textContent = "Update Query";
            this.modalSubmitButtonTarget.textContent = "Update";
            this.queryNameInputTarget.value = this.currentQueryName || "";
            this.queryDescriptionInputTarget.value =
                this.currentQueryDescription || "";
            this.queryOrgIdInputTarget.value = this.currentQueryOrgId || "";
        } else {
            this.modalTitleTarget.textContent = "Save Query";
            this.modalSubmitButtonTarget.textContent = "Save";
            this.queryNameInputTarget.value = "";
            this.queryDescriptionInputTarget.value = "";
            this.queryOrgIdInputTarget.value = "";
        }

        this.openModal();
    }

    openModal() {
        this.modalTarget.style.display = "block";
        this.queryNameInputTarget.focus();
    }

    closeModal() {
        this.modalTarget.style.display = "none";
        this.queryFormTarget.reset();
    }

    submitQueryForm(event) {
        event.preventDefault();

        const query = this.editor.getValue();
        const name = this.queryNameInputTarget.value.trim();
        const description = this.queryDescriptionInputTarget.value.trim();
        const organizationId = this.queryOrgIdInputTarget.value.trim();

        if (!name) {
            alert("Please enter a query name");
            return;
        }

        if (!organizationId) {
            alert("Please enter an organization ID");
            return;
        }

        if (this.currentQueryId) {
            // Update existing query
            fetch(`/admin/query-tool/saved/${this.currentQueryId}`, {
                method: "PUT",
                credentials: "same-origin",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    name: name,
                    description: description,
                    organization_id: organizationId,
                    query: query,
                }),
            })
                .then((response) => response.json())
                .then((data) => {
                    if (data.success) {
                        alert("Query updated successfully!");
                        this.closeModal();
                        this.resetQueryState();
                        this.refreshSavedQueries();
                    } else {
                        alert("Error updating query: " + data.error);
                    }
                })
                .catch((error) => {
                    alert("Error updating query: " + error.message);
                });
        } else {
            // Save new query
            fetch("/admin/query-tool/save", {
                method: "POST",
                credentials: "same-origin",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    name: name,
                    description: description,
                    organization_id: organizationId,
                    query: query,
                }),
            })
                .then((response) => response.json())
                .then((data) => {
                    if (data.success) {
                        alert("Query saved successfully!");
                        this.closeModal();
                        this.refreshSavedQueries();
                    } else {
                        alert("Error saving query: " + data.error);
                    }
                })
                .catch((error) => {
                    alert("Error saving query: " + error.message);
                });
        }
    }

    resetQueryState() {
        this.currentQueryId = null;
        this.currentQueryName = null;
        this.currentQueryDescription = null;
        this.currentQueryOrgId = null;
        this.saveQueryButtonTextTarget.textContent = "Save Query";
    }

    loadQuery(event) {
        const queryText = event.currentTarget.dataset.query;
        const queryId = event.currentTarget.dataset.queryId;
        const queryName = event.currentTarget.dataset.queryName;
        const queryDescription = event.currentTarget.dataset.queryDescription;
        const queryOrgId = event.currentTarget.dataset.queryOrgId;

        if (queryText) {
            this.editor.setValue(queryText);
            this.currentQueryId = queryId;
            this.currentQueryName = queryName;
            this.currentQueryDescription = queryDescription;
            this.currentQueryOrgId = queryOrgId;
            this.saveQueryButtonTextTarget.textContent = "Update Query";
        }
    }

    editQuery(event) {
        const queryId = event.currentTarget.dataset.queryId;
        const currentName = event.currentTarget.dataset.queryName;
        const currentDescription = event.currentTarget.dataset.queryDescription;
        const currentOrgId = event.currentTarget.dataset.queryOrgId;
        const currentQuery = event.currentTarget.dataset.query;

        // Load the query into the editor and set current query state
        this.editor.setValue(currentQuery);
        this.currentQueryId = queryId;
        this.currentQueryName = currentName;
        this.currentQueryDescription = currentDescription;
        this.currentQueryOrgId = currentOrgId;

        // Update button text to "Update Query"
        this.saveQueryButtonTextTarget.textContent = "Update Query";

        // Open the modal with the current values
        this.modalTitleTarget.textContent = "Update Query";
        this.modalSubmitButtonTarget.textContent = "Update";
        this.queryNameInputTarget.value = currentName || "";
        this.queryDescriptionInputTarget.value = currentDescription || "";
        this.queryOrgIdInputTarget.value = currentOrgId || "";

        this.openModal();
    }

    deleteQuery(event) {
        const queryId = event.currentTarget.dataset.queryId;
        if (!confirm("Are you sure you want to delete this query?")) {
            return;
        }

        fetch(`/admin/query-tool/saved/${queryId}`, {
            method: "DELETE",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json",
            },
        })
            .then((response) => response.json())
            .then((data) => {
                if (data.success) {
                    alert("Query deleted successfully!");
                    this.refreshSavedQueries();
                } else {
                    alert("Error deleting query: " + data.error);
                }
            })
            .catch((error) => {
                alert("Error deleting query: " + error.message);
            });
    }

    createQueryRow(query) {
        const row = document.createElement("tr");
        row.dataset.queryId = query.id;

        // Format organization display
        let orgDisplay = '—';
        if (query.organization_id && query.organization_name) {
            orgDisplay = `${this.escapeHtml(query.organization_name)} (${query.organization_id})`;
        } else if (query.organization_id) {
            orgDisplay = query.organization_id;
        }

        row.innerHTML = `
            <td>${this.escapeHtml(query.name || "")}</td>
            <td>${this.escapeHtml(query.description || "")}</td>
            <td>${orgDisplay}</td>
            <td>
                ${this.createActionButtons(query)}
            </td>
        `;

        return row;
    }

    createActionButtons(query) {
        const escapedQuery = this.escapeHtml(query.query);
        const escapedName = this.escapeHtml(query.name || "");
        const escapedDescription = this.escapeHtml(query.description || "");
        const orgId = query.organization_id || "";

        return `
            <button 
                class="button-raised" 
                data-action="click->admin--data-catalog#loadQuery"
                data-query-id="${query.id}"
                data-query-name="${escapedName}"
                data-query-description="${escapedDescription}"
                data-query-org-id="${orgId}"
                data-query="${escapedQuery}">
                Load
            </button>
            <button 
                class="button-flat" 
                data-action="click->admin--data-catalog#editQuery"
                data-query-id="${query.id}"
                data-query-name="${escapedName}"
                data-query-description="${escapedDescription}"
                data-query-org-id="${orgId}"
                data-query="${escapedQuery}">
                Edit
            </button>
            <button 
                class="button-flat" 
                data-action="click->admin--data-catalog#deleteQuery"
                data-query-id="${query.id}">
                Delete
            </button>
        `;
    }

    refreshSavedQueries() {
        fetch("/admin/query-tool/saved", {
            method: "GET",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json",
            },
        })
            .then((response) => response.json())
            .then((queries) => {
                this.renderQueriesTable(queries);
            })
            .catch((error) => {
                console.error("Error refreshing saved queries:", error);
            });
    }

    renderQueriesTable(queries) {
        const tbody = this.savedQueriesTableTarget.querySelector("tbody");
        tbody.innerHTML = "";

        if (queries.length === 0) {
            tbody.innerHTML =
                '<tr><td colspan="5" style="text-align: center;">No saved queries yet</td></tr>';
        } else {
            queries.forEach((query) => {
                tbody.appendChild(this.createQueryRow(query));
            });
        }
    }

    escapeHtml(text) {
        const div = document.createElement("div");
        div.textContent = text;
        return div.innerHTML;
    }
}
